import React, { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import { Input, Button, Modal } from '../../../../../components';
import { useAppDispatch, useAppSelector } from '../../../../../redux/Store';
import { addOwnedAgentWallet } from '../../../../../redux/thunks/agentThunks';
import {
  selectWalletSaveStatus,
  selectWalletSaveRejection,
} from '../../../../../redux/selectors/agentSelectors';
import {
  AGENT_CHAINS,
  labelForChain,
} from '../../../../../components/AgentChainChips/agentChainLabels';

interface AddWalletModalProps {
  isOpen: boolean;
  agentUserId: string;
  onClose: () => void;
}

/**
 * Add-wallet composer as a modal. Same shape as AddEndpointModal —
 * own state, own submit, closes on success, duplicate address
 * surfaces inline via walletSaveRejection.
 *
 * Chain defaults to KLV since every agent has that wallet post
 * migration 1789500000000 backfill; explicit new chains (ETH, Base,
 * etc.) are picked by the owner.
 */
export const AddWalletModal: React.FC<AddWalletModalProps> = ({
  isOpen,
  agentUserId,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const walletSaveStatus = useAppSelector(selectWalletSaveStatus);
  const walletSaveRejection = useAppSelector(selectWalletSaveRejection);

  const [chain, setChain] = useState<string>('klv');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');

  const reset = () => {
    setChain('klv');
    setAddress('');
    setLabel('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    const result = await dispatch(
      addOwnedAgentWallet({
        agentUserId,
        wallet: {
          chain,
          address: address.trim(),
          label: label.trim().length > 0 ? label.trim() : null,
        },
      }),
    );
    if (result.meta.requestStatus === 'fulfilled') {
      toast.success('Wallet added');
      handleClose();
    } else {
      const payload = result.payload as
        | { kind: string; message: string }
        | undefined;
      // Duplicate surfaces inline via walletSaveRejection on the
      // address input; other kinds toast.
      if (payload && payload.kind !== 'duplicate') {
        toast.error(payload.message || 'Failed to add wallet');
      }
    }
  };

  const isSaving = walletSaveStatus === 'saving';

  return (
    <Modal
      isOpen={isOpen}
      title="Add wallet"
      onClose={handleClose}
      size="md"
      // See AddEndpointModal for the .edit-agent scope rationale.
      className="edit-agent"
    >
      <form onSubmit={handleSubmit} className="edit-agent__form" noValidate>
        <div className="edit-agent__composer-row">
          <label className="input-wrapper edit-agent__method-picker">
            <span className="input-label">Chain</span>
            <select
              className="input-field"
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              disabled={isSaving}
            >
              {AGENT_CHAINS.map((c) => (
                <option key={c} value={c}>{labelForChain(c)}</option>
              ))}
            </select>
          </label>
          <Input
            label="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={60}
            placeholder="e.g. cold storage, ops account"
            disabled={isSaving}
          />
        </div>
        <Input
          label="Address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          maxLength={256}
          placeholder={
            chain === 'klv'
              ? 'klv1...'
              : chain === 'btc'
                ? '1... / bc1...'
                : '0x...'
          }
          required
          disabled={isSaving}
          error={
            walletSaveRejection?.kind === 'duplicate'
              ? walletSaveRejection.message
              : undefined
          }
        />

        <div className="edit-agent__actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSaving || !address.trim()}
          >
            {isSaving ? 'Adding…' : 'Add wallet'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddWalletModal;
